import {
  collection,
  getDocs,
  getDoc,
  doc,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';

// -------------------- Image upload --------------------
export const uploadImage = async (file, folder = 'uploads') => {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `${folder}/${Date.now()}-${safeName}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
};

// -------------------- Products --------------------
export const getProducts = async () => {
  const snap = await getDocs(collection(db, 'products'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const getProduct = async (id) => {
  const snap = await getDoc(doc(db, 'products', id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const addProduct = async (data) => {
  const ref = await addDoc(collection(db, 'products'), { ...data, createdAt: serverTimestamp() });
  return { id: ref.id, ...data };
};

export const updateProduct = async (id, data) => {
  await updateDoc(doc(db, 'products', id), data);
};

export const deleteProduct = async (id) => {
  await deleteDoc(doc(db, 'products', id));
};

// -------------------- Categories --------------------
export const getCategories = async () => {
  const snap = await getDocs(collection(db, 'categories'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const addCategory = async (data) => {
  const ref = await addDoc(collection(db, 'categories'), data);
  return { id: ref.id, ...data };
};

export const updateCategory = async (id, data) => {
  await updateDoc(doc(db, 'categories', id), data);
};

export const deleteCategory = async (id) => {
  await deleteDoc(doc(db, 'categories', id));
};

// -------------------- Orders --------------------
export const getOrders = async () => {
  const q = query(collection(db, 'orders'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const getUserOrders = async (userId) => {
  // Sorted client-side rather than via `orderBy` in the query — a where + orderBy on
  // different fields needs a Firestore composite index, which this project never had
  // configured, so the query threw on every call and silently left orders empty.
  const q = query(collection(db, 'orders'), where('userId', '==', userId));
  const snap = await getDocs(q);
  const orders = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  orders.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
  return orders;
};

export const createOrder = async (data) => {
  const ref = await addDoc(collection(db, 'orders'), { ...data, createdAt: serverTimestamp() });
  return { id: ref.id, ...data };
};

export const updateOrderStatus = async (id, status) => {
  await updateDoc(doc(db, 'orders', id), { status, updatedAt: serverTimestamp() });
};

// -------------------- Users --------------------
export const getUsers = async () => {
  const snap = await getDocs(collection(db, 'users'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const getUserProfile = async (uid) => {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const createUserProfile = async (uid, data) => {
  await setDoc(doc(db, 'users', uid), { ...data, createdAt: serverTimestamp() });
};

export const updateUserProfile = async (uid, data) => {
  await updateDoc(doc(db, 'users', uid), data);
};

export const deleteUser = async (uid) => {
  await deleteDoc(doc(db, 'users', uid));
};

// -------------------- Reviews & Testimonials --------------------
// A single collection covers both: a doc with a productId is a product review
// (shown on that product's page); a doc with productId left empty/null is a
// homepage testimonial (shown in the Customer Reviews carousel).
export const getReviews = async () => {
  const snap = await getDocs(collection(db, 'reviews'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const addReview = async (data) => {
  const ref = await addDoc(collection(db, 'reviews'), { ...data, createdAt: serverTimestamp() });
  return { id: ref.id, ...data };
};

export const updateReview = async (id, data) => {
  await updateDoc(doc(db, 'reviews', id), data);
};

export const deleteReview = async (id) => {
  await deleteDoc(doc(db, 'reviews', id));
};

// -------------------- Lessons (blog/SEO articles) --------------------
// Not linked from the main nav — posted from the admin panel purely so each
// article has a public, indexable /lessons/:slug URL for SEO.
export const getLessons = async () => {
  const snap = await getDocs(collection(db, 'lessons'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const addLesson = async (data) => {
  const ref = await addDoc(collection(db, 'lessons'), { ...data, createdAt: serverTimestamp() });
  return { id: ref.id, ...data };
};

export const updateLesson = async (id, data) => {
  await updateDoc(doc(db, 'lessons', id), data);
};

export const deleteLesson = async (id) => {
  await deleteDoc(doc(db, 'lessons', id));
};

// -------------------- Offers (promotional banners) --------------------
// A simple banner+tag promo system: each offer has a banner image and either targets
// a whole category or a hand-picked list of products. No automatic price math — this
// just drives the homepage banner(s) and an "Offer" badge on matching product cards.
export const getOffers = async () => {
  const snap = await getDocs(collection(db, 'offers'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const addOffer = async (data) => {
  const ref = await addDoc(collection(db, 'offers'), { ...data, createdAt: serverTimestamp() });
  return { id: ref.id, ...data };
};

export const updateOffer = async (id, data) => {
  await updateDoc(doc(db, 'offers', id), data);
};

export const deleteOffer = async (id) => {
  await deleteDoc(doc(db, 'offers', id));
};

// -------------------- Coupons --------------------
// Percent or fixed-amount discount codes, redeemed at checkout.
export const getCoupons = async () => {
  const snap = await getDocs(collection(db, 'coupons'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const addCoupon = async (data) => {
  const ref = await addDoc(collection(db, 'coupons'), { ...data, createdAt: serverTimestamp() });
  return { id: ref.id, ...data };
};

export const updateCoupon = async (id, data) => {
  await updateDoc(doc(db, 'coupons', id), data);
};

export const deleteCoupon = async (id) => {
  await deleteDoc(doc(db, 'coupons', id));
};

// -------------------- Site Settings --------------------
// A single document holding admin-editable site-wide media: the optional hero section
// background image and the packaging option photos (pouch / wooden box) shown on the
// product page. Missing fields just mean "use the built-in default" at the call site.
export const getSettings = async () => {
  const snap = await getDoc(doc(db, 'settings', 'site'));
  return snap.exists() ? snap.data() : {};
};

export const updateSettings = async (data) => {
  await setDoc(doc(db, 'settings', 'site'), data, { merge: true });
};

// -------------------- Seeding --------------------
export const seedIfEmpty = async (productsData, categoriesData) => {
  const catSnap = await getDocs(collection(db, 'categories'));
  const prodSnap = await getDocs(collection(db, 'products'));

  if (catSnap.empty) {
    const batch = writeBatch(db);
    categoriesData.forEach((c) => {
      const ref = doc(collection(db, 'categories'), c.id);
      batch.set(ref, c);
    });
    await batch.commit();
  }

  if (prodSnap.empty) {
    // Firestore batch limit is 500
    const chunks = [];
    for (let i = 0; i < productsData.length; i += 400) {
      chunks.push(productsData.slice(i, i + 400));
    }
    for (const chunk of chunks) {
      const batch = writeBatch(db);
      chunk.forEach((p) => {
        const ref = doc(collection(db, 'products'), p.id);
        batch.set(ref, { ...p, createdAt: new Date().toISOString() });
      });
      await batch.commit();
    }
  }
  return { seededCategories: catSnap.empty, seededProducts: prodSnap.empty };
};

// Destructively wipes every category and product doc, then writes the given
// data back in. Used to migrate to a new category structure — must be
// triggered explicitly by an authenticated admin, never run automatically.
export const resetCategoriesAndProducts = async (productsData, categoriesData) => {
  const catSnap = await getDocs(collection(db, 'categories'));
  const prodSnap = await getDocs(collection(db, 'products'));

  const deleteInChunks = async (docs) => {
    for (let i = 0; i < docs.length; i += 400) {
      const batch = writeBatch(db);
      docs.slice(i, i + 400).forEach((d) => batch.delete(d.ref));
      await batch.commit();
    }
  };
  await deleteInChunks(catSnap.docs);
  await deleteInChunks(prodSnap.docs);

  const catBatch = writeBatch(db);
  categoriesData.forEach((c) => {
    catBatch.set(doc(collection(db, 'categories'), c.id), c);
  });
  await catBatch.commit();

  for (let i = 0; i < productsData.length; i += 400) {
    const batch = writeBatch(db);
    productsData.slice(i, i + 400).forEach((p) => {
      batch.set(doc(collection(db, 'products'), p.id), { ...p, createdAt: new Date().toISOString() });
    });
    await batch.commit();
  }

  return { deletedCategories: catSnap.size, deletedProducts: prodSnap.size, newCategories: categoriesData.length, newProducts: productsData.length };
};
